/**
 * MobileWarningsList
 *
 * Mobile card-based replacement for WarningsDataTable.
 * Shows each warning as a touch-friendly card with key info.
 */
import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Pagination,
  Divider,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import moment from "moment";
import Langs, { Localize } from "../../../app/lang/langs";
import { checkPermission, capitalize } from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";

const PAGE_SIZE = 20;

// Category color mapping
const getCategoryColor = (category) => {
  switch (category) {
    case "warning":
      return "warning";
    case "recall the order":
      return "info";
    case "formal notice":
      return "error";
    case "Mise a pied":
    case "Mise à pied":
      return "error";
    default:
      return "default";
  }
};

// Status color mapping
const getStatusColor = (status) => {
  switch (status) {
    case "signed":
      return "success";
    case "sent":
      return "info";
    case "received":
      return "warning";
    default:
      return "default";
  }
};

/**
 * @param {Object} props
 * @param {Object} props.apiRef - MUI useGridApiRef() ref (kept for interface compat)
 * @param {Array}  props.data - warnings list
 * @param {string} props.scrollHeight - CSS height (kept for interface compat)
 * @param {Function} props.onViewDetails - view warning details
 * @param {Function} props.onDelete - delete warning by id
 */
export default function MobileWarningsList({
  apiRef,
  data,
  scrollHeight,
  onViewDetails,
  onDelete,
}) {
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);

  const hasEditPermission = checkPermission(
    config.scopes.warnings,
    config.permissionTypes.edit
  );

  const filteredData = useMemo(() => {
    const items = data || [];
    if (!searchText.trim()) return items;
    const terms = searchText.toLowerCase().split(" ").filter(Boolean);
    return items.filter((w) => {
      const searchStr = [
        w.collab_nom,
        w.collab_prenom,
        w.collab_matricule,
        w.category,
        w.status,
        w.team_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return terms.every((t) => searchStr.includes(t));
    });
  }, [data, searchText]);

  React.useEffect(() => {
    setPage(1);
  }, [searchText]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const paginatedData = filteredData.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        pb: 2.5,
        mt: "130px",
        height: "calc(100vh - 130px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search bar */}
      <TextField
        fullWidth
        size="small"
        placeholder={Localize("Search") + "..."}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1.5,
          "& input": { fontSize: "16px" },
          "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            bgcolor: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(12px)",
            transition: TRANSITION_SMOOTH,
            "& fieldset": {
              borderColor: "rgba(0,0,0,0.06)",
              transition: TRANSITION_SMOOTH,
            },
            "&:hover fieldset": {
              borderColor: "rgba(0,0,0,0.15)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "warning.main",
              boxShadow: "0 0 0 3px rgba(255,144,21,0.15)",
            },
          },
        }}
      />

      {/* Results count */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, px: 0.5, fontWeight: 500, letterSpacing: "0.02em", opacity: 0.7 }}
      >
        {filteredData.length} {Localize("results")}
      </Typography>

      {/* Card list */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.12)",
            borderRadius: 3,
          },
        }}
      >
        {paginatedData.map((warning, index) => (
          <Fade in key={warning.id} timeout={200 + index * 40}>
            <Card
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: "16px",
                overflow: "hidden",
                bgcolor: "rgba(255,255,255,0.85)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.06)",
                transition: "transform 250ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                "&:active": {
                  transform: "scale(0.98)",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                },
              }}
            >
              <CardActionArea
                onClick={() => hasEditPermission && onViewDetails(warning)}
                sx={{ p: 0 }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  {/* Name + date */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 0.75,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, fontSize: "0.9rem", letterSpacing: "-0.01em" }}
                    >
                      {warning.collab_prenom} {warning.collab_nom}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {warning.warning_date
                        ? moment(warning.warning_date).format("DD/MM/YYYY")
                        : ""}
                    </Typography>
                  </Box>

                  {/* Matricule */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75, opacity: 0.75, letterSpacing: "0.01em" }}
                  >
                    {warning.collab_matricule}
                    {warning.team_name ? ` - ${warning.team_name}` : ""}
                  </Typography>

                  {/* Tags */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {warning.category && (
                      <Chip
                        label={Localize(warning.category)}
                        size="small"
                        color={getCategoryColor(warning.category)}
                        sx={{ fontSize: "0.7rem", height: 24, fontWeight: 600, borderRadius: "8px", transition: TRANSITION_SMOOTH }}
                      />
                    )}
                    {warning.status && (
                      <Chip
                        label={Localize(warning.status)}
                        size="small"
                        color={getStatusColor(warning.status)}
                        variant="outlined"
                        sx={{ fontSize: "0.7rem", height: 24, fontWeight: 600, borderRadius: "8px", transition: TRANSITION_SMOOTH }}
                      />
                    )}
                  </Box>

                  {/* Comments preview */}
                  {warning.comments && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mt: 0.75,
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontStyle: "italic",
                        opacity: 0.6,
                      }}
                    >
                      {warning.comments}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>

              {/* Action buttons */}
              {hasEditPermission && (
                <>
                  <Divider sx={{ opacity: 0.4, borderColor: "rgba(0,0,0,0.06)" }} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      px: 1,
                      py: 0.25,
                    }}
                  >
                    <Tooltip title={<Langs str="VIEW_DETAILS" />}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewDetails(warning);
                        }}
                        sx={{
                          minWidth: 44,
                          minHeight: 44,
                          borderRadius: "12px",
                          transition: TRANSITION_SMOOTH,
                          "&:hover": {
                            backgroundColor: "rgba(255,144,21,0.08)",
                            transform: "scale(1.08)",
                          },
                          "&:active": { transform: "scale(0.95)" },
                        }}
                      >
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={<Langs str="DELETE_WARNING" />}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(warning.id);
                        }}
                        sx={{
                          minWidth: 44,
                          minHeight: 44,
                          borderRadius: "12px",
                          transition: TRANSITION_SMOOTH,
                          "&:hover": {
                            backgroundColor: "rgba(211,47,47,0.08)",
                            transform: "scale(1.08)",
                          },
                          "&:active": { transform: "scale(0.95)" },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </>
              )}
            </Card>
          </Fade>
        ))}

        {paginatedData.length === 0 && (
          <Fade in timeout={400}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.5 }}>
                <Langs str="No results" />
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                transition: TRANSITION_SMOOTH,
                "&:hover": { transform: "scale(1.08)" },
                "&:active": { transform: "scale(0.95)" },
                "&.Mui-selected": {
                  boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
